"use client";

import React, { useState } from "react";
import { Badge } from "@/components/ui/feedback";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import {
  Label,
  Input,
  Select,
  ErrorMessage,
} from "@/components/ui/form-elements";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/overlays";
import {
  createDecisionAction,
  verifyDecisionAction,
  deleteDecisionAction,
  updateDecisionAction,
} from "@/actions/decision-actions";
import { useRouter } from "next/navigation";
import Link from "next/link";
import {
  Sparkles,
  Plus,
  Search,
  Calendar,
  User,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Trash2,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Edit2,
} from "lucide-react";

interface DecisionItem {
  id: string;
  title: string;
  context: string | null;
  verificationDeadline: string;
  deltaResult: number | null;
  status: "pending" | "verified";
  createdAt: string;
  deciderName: string | null;
  deciderId: string | null;
  targetGoalTitle: string | null;
  targetGoalId: string | null;
  targetKpiTitle: string | null;
  targetKpiId: string | null;
}

interface AgentItem {
  id: string;
  name: string;
}

interface GoalItem {
  id: string;
  title: string;
}

interface KpiItem {
  id: string;
  title: string;
  value: string;
  unit: string;
}

interface DecisionsClientProps {
  initialDecisions: DecisionItem[];
  agents: AgentItem[];
  goals: GoalItem[];
  kpis: KpiItem[];
}

export function DecisionsClient({
  initialDecisions,
  agents,
  goals,
  kpis,
}: DecisionsClientProps) {
  const router = useRouter();
  const [decisionsList, setDecisionsList] =
    useState<DecisionItem[]>(initialDecisions);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  // Modals state
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isVerifyModalOpen, setIsVerifyModalOpen] = useState(false);
  const [selectedDecision, setSelectedDecision] = useState<DecisionItem | null>(
    null,
  );

  // Form states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editingDecision, setEditingDecision] = useState<DecisionItem | null>(
    null,
  );

  // New decision state
  const [newTitle, setNewTitle] = useState("");
  const [newContext, setNewContext] = useState("");
  const [newDeciderId, setNewDeciderId] = useState("");
  const [newGoalId, setNewGoalId] = useState("");
  const [newKpiId, setNewKpiId] = useState("");
  const [newDeadline, setNewDeadline] = useState("");

  // Verification state
  const [deltaValue, setDeltaValue] = useState("");

  React.useEffect(() => {
    setDecisionsList(initialDecisions);
  }, [initialDecisions]);

  const handleOpenAddModal = (decision?: DecisionItem) => {
    setError("");
    setLoading(false);
    if (decision) {
      setEditingDecision(decision);
      setNewTitle(decision.title);
      setNewContext(decision.context || "");
      setNewDeciderId(decision.deciderId || "");
      setNewGoalId(decision.targetGoalId || "");
      setNewKpiId(decision.targetKpiId || "");
      setNewDeadline(decision.verificationDeadline.split("T")[0]);
    } else {
      setEditingDecision(null);
      setNewTitle("");
      setNewContext("");
      setNewDeciderId("");
      setNewGoalId("");
      setNewKpiId("");
      const defaultDeadline = new Date();
      defaultDeadline.setDate(defaultDeadline.getDate() + 30);
      setNewDeadline(defaultDeadline.toISOString().split("T")[0]);
    }
    setIsAddModalOpen(true);
  };

  const handleCreateDecision = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!newTitle.trim()) {
      setError("Il titolo della decisione è obbligatorio.");
      return;
    }
    if (!newDeadline) {
      setError("La data di verifica è obbligatoria.");
      return;
    }

    setLoading(true);
    try {
      let res;
      if (editingDecision) {
        res = await updateDecisionAction(editingDecision.id, {
          title: newTitle,
          context: newContext || null,
          deciderId: newDeciderId || null,
          targetGoalId: newGoalId || null,
          targetKpiId: newKpiId || null,
          verificationDeadline: newDeadline,
          status: editingDecision.status,
          deltaResult: editingDecision.deltaResult,
        });
      } else {
        res = await createDecisionAction({
          title: newTitle,
          context: newContext,
          deciderId: newDeciderId || null,
          targetGoalId: newGoalId || null,
          targetKpiId: newKpiId || null,
          verificationDeadline: newDeadline,
        });
      }

      if (res.error) {
        setError(res.error);
      } else {
        setIsAddModalOpen(false);
        router.refresh();
      }
    } catch (err: any) {
      setError(err.message || "Errore durante il salvataggio.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenVerifyModal = (decision: DecisionItem) => {
    setSelectedDecision(decision);
    setDeltaValue("");
    setError("");
    setIsVerifyModalOpen(true);
  };

  const handleVerifyDecision = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!selectedDecision) return;
    const val = parseFloat(deltaValue);
    if (isNaN(val)) {
      setError("Inserisci un valore numerico valido (es. 12.5 o -3.2).");
      return;
    }

    setLoading(true);
    try {
      const res = await verifyDecisionAction(selectedDecision.id, val);
      if (res.error) {
        setError(res.error);
      } else {
        setIsVerifyModalOpen(false);
        router.refresh();
      }
    } catch (err: any) {
      setError(err.message || "Errore durante la verifica.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDecision = async (id: string) => {
    if (!confirm("Sei sicuro di voler eliminare questa decisione dal log?"))
      return;
    try {
      await deleteDecisionAction(id);
      router.refresh();
    } catch (err: any) {
      alert("Impossibile eliminare la decisione: " + err.message);
    }
  };

  const filteredDecisions = decisionsList.filter((dec) => {
    const matchesSearch =
      dec.title.toLowerCase().includes(search.toLowerCase()) ||
      (dec.context && dec.context.toLowerCase().includes(search.toLowerCase()));
    const matchesStatus = statusFilter === "all" || dec.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString: string) => {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return "N/A";
    return d.toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const isOverdue = (deadlineStr: string, status: "pending" | "verified") => {
    if (status === "verified") return false;
    const deadline = new Date(deadlineStr);
    return deadline < new Date();
  };

  return (
    <div className="space-y-6">
      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between bg-card p-4 rounded-xl border border-border">
        <div className="relative w-full sm:max-w-xs">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-text-muted" />
          <Input
            placeholder="Cerca decisioni..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="flex w-full sm:w-auto gap-3 items-center justify-between sm:justify-end">
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-[180px]"
          >
            <option value="all">Tutti gli stati</option>
            <option value="pending">In attesa (Pending)</option>
            <option value="verified">Verificati (Verified)</option>
          </Select>

          <Button
            onClick={() => handleOpenAddModal()}
            className="gap-1.5 shrink-0"
          >
            <Plus className="h-4 w-4" />
            Nuova Decisione
          </Button>
        </div>
      </div>

      {/* Grid List */}
      {filteredDecisions.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-12 border border-dashed border-border rounded-2xl bg-card/50 text-center max-w-xl mx-auto my-8">
          <Activity className="h-10 w-10 text-primary-500 mb-3 animate-pulse" />
          <h3 className="text-base font-bold text-text-primary">
            Nessuna decisione trovata
          </h3>
          <p className="text-sm text-text-secondary mt-1">
            Non ci sono decisioni registrate corrispondenti ai filtri
            selezionati.
          </p>
          <Button onClick={() => handleOpenAddModal()} className="mt-4 gap-1.5">
            <Plus className="h-4 w-4" />
            Registra Prima Decisione
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {filteredDecisions.map((dec) => {
            const overdue = isOverdue(dec.verificationDeadline, dec.status);
            return (
              <Card
                key={dec.id}
                onClick={() => handleOpenAddModal(dec)}
                className="group relative overflow-hidden flex flex-col md:flex-row md:items-center justify-between hover:border-primary-500/50 transition-all border-l-4 border-l-primary-500 cursor-pointer"
              >
                {/* Hover actions Graph/Pencil/Trash container */}
                <div
                  className="absolute right-3 top-3 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1.5 pointer-events-none group-hover:pointer-events-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Link
                    href={`/explore?nodeId=${dec.id}`}
                    className="p-1 hover:bg-card-muted text-text-secondary hover:text-primary-600 rounded transition-colors"
                    title="Grafo"
                  >
                    <Activity className="h-3.5 w-3.5" />
                  </Link>
                  <button
                    onClick={() => handleOpenAddModal(dec)}
                    className="p-1 hover:bg-card-muted text-text-secondary hover:text-foreground rounded transition-colors"
                    title="Modifica"
                  >
                    <Edit2 className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => handleDeleteDecision(dec.id)}
                    className="p-1 hover:bg-card-muted text-error hover:text-error/80 rounded transition-colors"
                    title="Elimina"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>

                <CardContent className="p-6 flex-1 space-y-4 md:space-y-0 md:grid md:grid-cols-4 md:gap-4 md:items-center w-full">
                  {/* Decision Title & Context */}
                  <div className="md:col-span-2 space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-bold text-text-muted uppercase tracking-wider">
                        ID: {dec.id}
                      </span>
                      {dec.status === "verified" ? (
                        <Badge
                          variant="success"
                          className="px-2 py-0.5 text-[10px]"
                        >
                          Verificato
                        </Badge>
                      ) : (
                        <Badge
                          variant="warning"
                          className="px-2 py-0.5 text-[10px]"
                        >
                          In Attesa
                        </Badge>
                      )}
                      {overdue && (
                        <Badge
                          variant="error"
                          className="px-2 py-0.5 text-[10px] flex items-center gap-1"
                        >
                          <AlertTriangle className="h-3 w-3" /> Scaduto
                        </Badge>
                      )}
                    </div>
                    <h3 className="text-base font-bold text-text-primary leading-snug hover:text-primary-600 transition-colors">
                      <Link
                        href={`/explore?nodeId=${dec.id}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {dec.title}
                      </Link>
                    </h3>
                    {dec.context && (
                      <p className="text-xs text-text-secondary leading-relaxed max-w-xl">
                        {dec.context}
                      </p>
                    )}
                  </div>

                  {/* Metadata (Decider & Target) */}
                  <div className="space-y-2 text-xs text-text-secondary">
                    <div className="flex items-center gap-1.5">
                      <User className="h-3.5 w-3.5 text-text-muted" />
                      {dec.deciderId ? (
                        <Link
                          href={`/explore?nodeId=${dec.deciderId}`}
                          onClick={(e) => e.stopPropagation()}
                          className="hover:text-primary-600 hover:underline transition-colors"
                        >
                          Deciso da: <strong>{dec.deciderName}</strong>
                        </Link>
                      ) : (
                        <span>
                          Deciso da:{" "}
                          <strong>{dec.deciderName || "Sistema"}</strong>
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5 text-text-muted" />
                      <span>
                        Verifica:{" "}
                        <strong>{formatDate(dec.verificationDeadline)}</strong>
                      </span>
                    </div>
                    {dec.targetGoalTitle && (
                      <div className="flex items-center gap-1.5">
                        <span className="text-[9px] uppercase px-1 border border-category-strategy-border bg-category-strategy-soft text-category-strategy font-bold rounded">
                          Goal
                        </span>
                        {dec.targetGoalId ? (
                          <Link
                            href={`/explore?nodeId=${dec.targetGoalId}`}
                            onClick={(e) => e.stopPropagation()}
                            className="truncate max-w-[150px] hover:text-primary-600 hover:underline transition-colors"
                            title={dec.targetGoalTitle}
                          >
                            {dec.targetGoalTitle}
                          </Link>
                        ) : (
                          <span
                            className="truncate max-w-[150px]"
                            title={dec.targetGoalTitle}
                          >
                            {dec.targetGoalTitle}
                          </span>
                        )}
                      </div>
                    )}
                    {dec.targetKpiTitle && (
                      <div className="flex items-center gap-1.5">
                        <span className="text-[9px] uppercase px-1 border border-category-finance-border bg-category-finance-soft text-category-finance font-bold rounded">
                          KPI
                        </span>
                        {dec.targetKpiId ? (
                          <Link
                            href={`/explore?nodeId=${dec.targetKpiId}`}
                            onClick={(e) => e.stopPropagation()}
                            className="truncate max-w-[150px] hover:text-primary-600 hover:underline transition-colors"
                            title={dec.targetKpiTitle}
                          >
                            {dec.targetKpiTitle}
                          </Link>
                        ) : (
                          <span
                            className="truncate max-w-[150px]"
                            title={dec.targetKpiTitle}
                          >
                            {dec.targetKpiTitle}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Impact Results / Action */}
                  <div
                    className="flex items-center justify-between md:justify-end gap-4 border-t border-border/40 md:border-t-0 pt-4 md:pt-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="text-right">
                      {dec.status === "verified" ? (
                        <div className="space-y-1">
                          <span className="text-[10px] text-text-secondary font-semibold uppercase block">
                            Impatto Rilevato
                          </span>
                          <span
                            className={`inline-flex items-center gap-1 text-base font-extrabold ${
                              (dec.deltaResult ?? 0) >= 0
                                ? "text-success"
                                : "text-error"
                            }`}
                          >
                            {(dec.deltaResult ?? 0) >= 0 ? (
                              <ArrowUpRight className="h-4 w-4" />
                            ) : (
                              <ArrowDownRight className="h-4 w-4" />
                            )}
                            {(dec.deltaResult ?? 0) >= 0 ? "+" : ""}
                            {dec.deltaResult}%
                          </span>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          <span className="text-[10px] text-text-secondary font-semibold uppercase block">
                            Verifica Impatto
                          </span>
                          <Button
                            size="sm"
                            className="h-8 px-3 text-xs"
                            onClick={() => handleOpenVerifyModal(dec)}
                          >
                            <Sparkles className="h-3 w-3 mr-1" /> Analizza
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add Decision Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingDecision
                ? "Modifica Decisione"
                : "Registra Decisione Strategica"}
            </DialogTitle>
            <DialogDescription>
              {editingDecision
                ? "Modifica i dettagli della decisione strategica."
                : "Aggiungi una decisione per misurarne l'impatto a 30/60/90 giorni correlandola con i KPI e gli obiettivi."}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateDecision} className="space-y-4 my-2">
            <div className="space-y-1.5">
              <Label htmlFor="dec-title">Titolo Decisione</Label>
              <Input
                id="dec-title"
                placeholder="es. Rilascio Checkout Stripe Sandbox"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                disabled={loading}
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="dec-context">Contesto / Descrizione</Label>
              <Input
                id="dec-context"
                placeholder="Spiega brevemente il razionale della decisione o i cambiamenti fatti..."
                value={newContext}
                onChange={(e) => setNewContext(e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="dec-agent">Proprietario (Decider)</Label>
                <Select
                  id="dec-agent"
                  value={newDeciderId}
                  onChange={(e) => setNewDeciderId(e.target.value)}
                  disabled={loading}
                >
                  <option value="">Seleziona chi decide</option>
                  {agents.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="dec-deadline">Data di Verifica</Label>
                <Input
                  id="dec-deadline"
                  type="date"
                  value={newDeadline}
                  onChange={(e) => setNewDeadline(e.target.value)}
                  disabled={loading}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="dec-goal">Obiettivo Strategico Target</Label>
                <Select
                  id="dec-goal"
                  value={newGoalId}
                  onChange={(e) => setNewGoalId(e.target.value)}
                  disabled={loading}
                >
                  <option value="">Nessuno</option>
                  {goals.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.title}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="dec-kpi">KPI Target Correlato</Label>
                <Select
                  id="dec-kpi"
                  value={newKpiId}
                  onChange={(e) => setNewKpiId(e.target.value)}
                  disabled={loading}
                >
                  <option value="">Nessuno</option>
                  {kpis.map((k) => (
                    <option key={k.id} value={k.id}>
                      {k.title} ({k.value} {k.unit})
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsAddModalOpen(false)}
                disabled={loading}
              >
                Annulla
              </Button>
              <Button type="submit" loading={loading}>
                {editingDecision ? "Salva Modifiche" : "Salva Decisione"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Verify Impact Modal */}
      <Dialog open={isVerifyModalOpen} onOpenChange={setIsVerifyModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Verifica Impatto Decisione</DialogTitle>
            <DialogDescription>
              Inserisci la variazione percentuale (delta) registrata
              post-decisione per la decisione{" "}
              <strong className="text-text-primary">
                "{selectedDecision?.title}"
              </strong>
              .
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleVerifyDecision} className="space-y-4 my-2">
            <div className="space-y-1.5">
              <Label htmlFor="delta-value">Delta Risultato (%)</Label>
              <Input
                id="delta-value"
                type="number"
                step="0.1"
                placeholder="es. 15.4 (per +15.4%) o -2.1 (per -2.1%)"
                value={deltaValue}
                onChange={(e) => setDeltaValue(e.target.value)}
                disabled={loading}
                required
              />
            </div>

            <div className="text-xs text-text-secondary leading-relaxed bg-card-muted/30 p-3 rounded-lg border border-border">
              <span className="font-semibold text-text-primary block mb-1">
                Come calcolare il Delta:
              </span>
              Confronta il valore del KPI/Goal rilevato 30 giorni prima rispetto
              al valore odierno. Ad esempio, se il tasso di conversione è
              passato da 2.0% a 2.3%, il delta è{" "}
              <strong className="text-success">+15.0%</strong>.
            </div>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsVerifyModalOpen(false)}
                disabled={loading}
              >
                Annulla
              </Button>
              <Button
                type="submit"
                loading={loading}
                className="bg-gradient-to-r from-primary-500 to-primary-600"
              >
                Registra Delta
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
